import { useEffect, useState } from 'react';
import { AirplaneTilt, X } from '@phosphor-icons/react';
import { aircraftType } from '../data/aircraft';
import { airportById } from '../data/airports';
import {
  isFeatureEnabled,
  engineeringAssistant,
  FLIGHT_SPEED_FEATURE_ID,
  cabinConfigAssistant,
  CABIN_CONFIG_FEATURE_ID,
  crewSalaryAssistant,
  CREW_SALARY_FEATURE_ID,
} from '../assistants';
import { aircraftMaintenanceCostWithCount } from '../game/commands';
import { forecastAircraftReplacement } from '../game/operationForecasts';
import type { GameState } from '../game/types';
import { crewEmotionLabel } from '../simulation/crew';
import { isAircraftDelivered } from '../simulation/leasing';
import { aircraftOperation, cabinWeightedSeats, fuelPriceIndex } from '../simulation/economy';
import { integer, kilometers, money, percent } from '../ui/format';

interface Props {
  game: GameState;
  aircraftId?: string;
  onClose: () => void;
  onMaintain: (aircraftId: string) => void;
  onSell: (aircraftId: string) => void;
  onReturnLease: (aircraftId: string) => void;
  onUnassignAircraft: (routeId: string, aircraftId: string) => void;
  onReplaceAircraft: (routeId: string, fromAircraftId: string, toAircraftId: string) => void;
  onSetSpeedModifier: (aircraftId: string, modifier: number) => void;
  onSetFareRatio: (aircraftId: string, fareRatio: number) => void;
  onSetCabinConfig: (aircraftId: string, cabinConfig: { economy: number; business: number; first: number }) => void;
  onSetCrewSalaryModifier: (aircraftId: string, modifier: number) => void;
}

export function AircraftDetailPanel({
  game,
  aircraftId,
  onClose,
  onMaintain,
  onSell,
  onReturnLease,
  onUnassignAircraft,
  onReplaceAircraft,
  onSetSpeedModifier,
  onSetFareRatio,
  onSetCabinConfig,
  onSetCrewSalaryModifier,
}: Props) {
  const [replacementAircraftId, setReplacementAircraftId] = useState('');
  const [replacementPickerOpen, setReplacementPickerOpen] = useState(false);

  useEffect(() => {
    if (!aircraftId) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [aircraftId, onClose]);

  const aircraft = aircraftId ? game.aircraft.find((item) => item.id === aircraftId) : undefined;
  if (!aircraft) return null;

  const type = aircraftType(aircraft.typeId);
  const typeCounts = game.aircraft.reduce((counts, item) => {
    counts.set(item.typeId, (counts.get(item.typeId) ?? 0) + 1);
    return counts;
  }, new Map<string, number>());
  const maintenanceCost = aircraftMaintenanceCostWithCount(aircraft, typeCounts.get(aircraft.typeId) ?? 1);
  const delivered = isAircraftDelivered(aircraft, game.week);
  const route = aircraft.routeIds.length ? game.routes.find((item) => item.id === aircraft.routeIds[0]) : undefined;
  const cycleRatio = aircraft.totalCycles / type.maxCycles;
  const hourRatio = aircraft.totalFlightHours / type.maxFlightHours;
  const lifeLimitLabel = cycleRatio > hourRatio ? '起降循环' : '飞行小时';
  const lifeLimitValue = cycleRatio > hourRatio ? cycleRatio : hourRatio;
  const conditionClass = aircraft.condition < 0.5 ? 'critical' : aircraft.condition < 0.7 ? 'warning' : 'healthy';
  const acquisitionLabel = aircraft.lease ? '租赁' : aircraft.acquisition === 'used' ? '二手购入' : '新机购入';
  const cabinConfig = aircraft.cabinConfig ?? type.cabins;
  const cabinDemand = route?.stats.cabins;
  const cabinWeightedTotal = cabinWeightedSeats(cabinConfig);
  const physicalCabinTotal = cabinConfig.economy + cabinConfig.business + cabinConfig.first;
  const routeRoundTrips = route ? aircraftOperation(aircraft, route.stats.distanceKm, fuelPriceIndex(game.week, game.seed)).roundTrips : 0;
  const idleReplacementCandidates = route
    ? game.aircraft.filter((candidate) => candidate.id !== aircraft.id && !candidate.routeIds.length && isAircraftDelivered(candidate, game.week))
    : [];
  const replacementRouteAirports = route && idleReplacementCandidates.length
    ? { origin: airportById(route.originId), destination: airportById(route.destinationId) }
    : undefined;
  const replacementCandidates = route && replacementRouteAirports
    ? idleReplacementCandidates.filter((candidate) => {
      const candidateType = aircraftType(candidate.typeId);
      return candidateType.rangeKm >= route.stats.distanceKm
        && candidateType.runwayClass <= replacementRouteAirports.origin.runwayClass
        && candidateType.runwayClass <= replacementRouteAirports.destination.runwayClass;
    })
    : [];
  const selectedReplacementId = replacementCandidates.some((candidate) => candidate.id === replacementAircraftId)
    ? replacementAircraftId
    : replacementCandidates[0]?.id ?? '';
  const selectedReplacement = replacementCandidates.find((candidate) => candidate.id === selectedReplacementId);
  const replacementForecast = route && selectedReplacement
    ? forecastAircraftReplacement(game, route.id, aircraft.id, selectedReplacement.id)
    : undefined;
  const speedUnlocked = isFeatureEnabled(game, FLIGHT_SPEED_FEATURE_ID, engineeringAssistant);
  const cabinUnlocked = isFeatureEnabled(game, CABIN_CONFIG_FEATURE_ID, cabinConfigAssistant);
  const crewUnlocked = isFeatureEnabled(game, CREW_SALARY_FEATURE_ID, crewSalaryAssistant);

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <section className="aircraft-detail-sheet" data-tutorial-aircraft-detail role="dialog" aria-modal="true" aria-labelledby="aircraft-detail-title" onClick={(event) => event.stopPropagation()}>
        <div className="sheet-handle" />
        <header className="settlement-header">
          <div className="settlement-plane-mark" style={{ '--plane-color': type.accent } as React.CSSProperties}>
            <AirplaneTilt weight="fill" size={30} />
          </div>
          <div>
            <span className="eyebrow">{acquisitionLabel} · {aircraft.id}</span>
            <h2 id="aircraft-detail-title">{type.name}</h2>
            <p>{type.family} · 购于第 {aircraft.acquiredWeek} 周</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="关闭"><X weight="bold" size={22} /></button>
        </header>

        <div className="aircraft-detail-hero">
          <div className="aircraft-detail-condition" data-tutorial-aircraft-condition style={{ '--score': `${aircraft.condition * 100}%` } as React.CSSProperties}>
            <span className="aircraft-detail-condition-track">
              <span className={`aircraft-detail-condition-fill ${conditionClass}`} />
            </span>
            <div>
              <strong>{Math.round(aircraft.condition * 100)}%</strong>
              <span>机况</span>
            </div>
          </div>
          <div className="aircraft-detail-status">
            <span className={route ? 'assigned' : delivered ? 'idle' : 'pending'}>
              {route ? `${route.originId}-${route.destinationId}` : delivered ? '空闲可用' : `第 ${aircraft.lease?.deliveryWeek} 周交付`}
            </span>
            <strong>{aircraft.lease ? `周租金 ${money(aircraft.lease.weeklyPayment)}` : `账面价值 ${money(aircraft.bookValue)}`}</strong>
          </div>
        </div>

        <div className="settlement-breakdown aircraft-detail-breakdown">
          <div><span>寿命消耗</span><strong>{percent(lifeLimitValue, 1)}</strong><small>{lifeLimitLabel}主导</small></div>
          <div><span>飞行小时</span><strong>{integer(aircraft.totalFlightHours)} h</strong><small>飞行小时 {percent(hourRatio, 1)}</small></div>
          <div><span>起降循环</span><strong>{integer(aircraft.totalCycles)}</strong><small>起降循环 {percent(cycleRatio, 1)}</small></div>
          <div><span>维护成本</span><strong>{money(maintenanceCost)}</strong></div>
          {crewUnlocked && <><div><span>机组情绪</span><strong>{crewEmotionLabel(aircraft.crewMood)}</strong></div><div><span>机组聘于</span><strong>第 {aircraft.crewHiredWeek} 周</strong></div></>}
          <div data-tutorial-aircraft-range><span>最大航程</span><strong>{kilometers(type.rangeKm)}</strong></div>
          <div data-tutorial-aircraft-runway><span>跑道等级</span><strong>L{type.runwayClass}</strong></div>
        </div>

        {route && (
          <div className="aircraft-detail-route">
            <span>当前执飞</span>
            <strong>{airportById(route.originId).nameZh} ↔ {airportById(route.destinationId).nameZh}</strong>
            <small>{route.originId}-{route.destinationId} · {kilometers(route.stats.distanceKm)} · 航线每周 {integer(route.stats.roundTrips)} 班 · 本机执飞 {integer(routeRoundTrips)} 班</small>
          </div>
        )}

        {route && (
          <div className="aircraft-fare-panel" data-tutorial-aircraft-fare>
            <label>
              <span>本机票价</span>
              <input className="aircraft-fare-number-input" type="number" min={0} step={5} value={Math.round(aircraft.pendingFareRatio ?? aircraft.fareRatio ?? route.pendingFareRatio ?? route.fareRatio ?? 100)} onChange={(event) => onSetFareRatio(aircraft.id, Number(event.target.value))} aria-label={`${type.name} 本机票价`} />
              <div className="aircraft-fare-stepper" aria-label={`${type.name} 本机票价调整`}>
                <button type="button" aria-label="降低本机票价" onClick={() => onSetFareRatio(aircraft.id, Math.max(0, Math.round((aircraft.pendingFareRatio ?? aircraft.fareRatio ?? route.pendingFareRatio ?? route.fareRatio ?? 100) - 5)))}>−</button>
                <strong>{Math.round(aircraft.pendingFareRatio ?? aircraft.fareRatio ?? route.pendingFareRatio ?? route.fareRatio ?? 100)}%</strong>
                <button type="button" aria-label="提高本机票价" onClick={() => onSetFareRatio(aircraft.id, Math.min(400, Math.round((aircraft.pendingFareRatio ?? aircraft.fareRatio ?? route.pendingFareRatio ?? route.fareRatio ?? 100) + 5)))}>＋</button>
              </div>
              <b>%</b>
            </label>
            <div>
              {[70, 100, 130].map((ratio) => (
                <button key={ratio} type="button" onClick={() => onSetFareRatio(aircraft.id, ratio)}>{ratio}%</button>
              ))}
            </div>
            <small>{aircraft.pendingFareRatio !== undefined ? `下周生效，当前 ${Math.round(aircraft.fareRatio ?? route.fareRatio ?? 100)}%` : '本机价格会和同航线所有航班竞争客户；航线总览改价会覆盖本机。'}</small>
          </div>
        )}

        {route && replacementPickerOpen && replacementCandidates.length > 0 && (
          <div className="aircraft-replacement-panel">
            <label>
              <span>替换飞机</span>
              <select
                value={selectedReplacementId}
                onChange={(event) => setReplacementAircraftId(event.target.value)}
                disabled={!replacementCandidates.length}
                aria-label="替换飞机"
              >
                {replacementCandidates.length ? replacementCandidates.map((candidate) => {
                  const candidateType = aircraftType(candidate.typeId);
                  return (
                    <option key={candidate.id} value={candidate.id}>
                      {candidateType.name}{candidate.lease ? ' · 租' : ''} · {candidate.id} · 航程 {kilometers(candidateType.rangeKm)}
                    </option>
                  );
                }) : (
                  <option value="">暂无可替换飞机</option>
                )}
              </select>
            </label>
            <small>
              {selectedReplacement ? `${aircraftType(selectedReplacement.typeId).family} · 机况 ${percent(selectedReplacement.condition, 0)} · 空闲可用` : ''}
            </small>
            {replacementForecast && (
              <small className={replacementForecast.weeklyProfitAfter < replacementForecast.weeklyProfitBefore ? 'red' : 'green'}>
                预测周利润 {money(replacementForecast.weeklyProfitBefore)} → {money(replacementForecast.weeklyProfitAfter)} · 载客率 {percent(replacementForecast.loadFactorAfter)}
              </small>
            )}
            <button
              className="primary-button secondary"
              disabled={!selectedReplacementId}
              onClick={() => {
                if (selectedReplacementId) onReplaceAircraft(route.id, aircraft.id, selectedReplacementId);
              }}
            >
              替换当前执飞飞机
            </button>
          </div>
        )}

        {cabinUnlocked && (
          <div className="aircraft-detail-config">
            <h3>客舱布局</h3>
            <div className="cabin-inputs">
              {(['economy', 'business', 'first'] as const).map((cabin) => (
                <label key={cabin}>
                  <span>{cabin === 'economy' ? '经济' : cabin === 'business' ? '商务' : '头等'}</span>
                  <input
                    type="number"
                    min={0}
                    max={type.maxEconomySeats}
                    value={cabinConfig[cabin]}
                    onChange={(event) => {
                      const next = { ...cabinConfig, [cabin]: Math.max(0, Number(event.target.value) || 0) };
                      if (cabinWeightedSeats(next) <= type.maxEconomySeats) {
                        onSetCabinConfig(aircraft.id, next);
                      }
                    }}
                    aria-label={`${type.name} ${cabin} 座位数`}
                  />
                  {cabinDemand && <small>当前需求 {integer(cabinDemand[cabin].demand)}</small>}
                </label>
              ))}
            </div>
            <small>当前 Y{cabinConfig.economy} / J{cabinConfig.business} / F{cabinConfig.first} · 物理座位 {physicalCabinTotal} · 全经济舱上限 {type.maxEconomySeats}</small>
          </div>
        )}

        <div className="aircraft-detail-tuning">
          {speedUnlocked && (
            <div className="asset-speed-control">
              <label>
                <span>巡航速度 {(aircraft.speedModifier * 100).toFixed(0)}% {route && `· 本机 ${integer(routeRoundTrips)} 班/周`}</span>
                <input
                  type="range"
                  min={0.92}
                  max={1.08}
                  step={0.01}
                  value={aircraft.speedModifier}
                  onChange={(event) => onSetSpeedModifier(aircraft.id, Number(event.target.value))}
                  aria-label={`${type.name} 巡航速度调整`}
                />
              </label>
              <small>{aircraft.speedModifier > 1 ? '提速：减少飞行小时，增加燃油' : aircraft.speedModifier < 1 ? '减速：节省燃油，增加飞行小时' : '标准巡航速度'}</small>
            </div>
          )}
          {crewUnlocked && (
            <div className="asset-crew-control">
              <label>
                <span>机组薪资 {(aircraft.crewSalaryModifier * 100).toFixed(0)}%</span>
                <input
                  type="range"
                  min={0.20}
                  max={1.50}
                  step={0.01}
                  value={aircraft.crewSalaryModifier}
                  onChange={(event) => onSetCrewSalaryModifier(aircraft.id, Number(event.target.value))}
                  aria-label={`${type.name} 机组薪资调整`}
                />
              </label>
              <small>{aircraft.crewSalaryModifier > 1 ? '高薪：提升服务，增加成本' : aircraft.crewSalaryModifier < 1 ? '降薪：节省成本，可能影响声誉' : '标准薪资'}</small>
            </div>
          )}
        </div>

        <div className="aircraft-detail-actions">
          <button
            className="primary-button"
            disabled={!delivered || aircraft.condition >= 0.995 || game.cash < maintenanceCost}
            onClick={() => onMaintain(aircraft.id)}
          >
            维护 {money(maintenanceCost)}
          </button>
          {!route && aircraft.lease && (
            <button
              className="primary-button secondary"
              onClick={() => {
                if (!window.confirm(`确认退租 ${type.name}？这会移除该飞机，并按规则退回部分押金。`)) return;
                onReturnLease(aircraft.id);
              }}
            >
              退租
            </button>
          )}
          {!route && !aircraft.lease && (
            <button
              className="primary-button secondary"
              onClick={() => {
                if (!window.confirm(`确认出售 ${type.name}？这会移除该飞机，并按当前账面价值折价回收现金。`)) return;
                onSell(aircraft.id);
              }}
            >
              出售
            </button>
          )}
          {route && (
            <button className="primary-button secondary" onClick={() => onUnassignAircraft(route.id, aircraft.id)}>撤下航线</button>
          )}
          {route && replacementCandidates.length > 0 && (
            <button className="primary-button secondary" onClick={() => setReplacementPickerOpen((open) => !open)}>
              {replacementPickerOpen ? '收起替换' : '替换飞机'}
            </button>
          )}
          <button className="secondary-action" onClick={onClose}>关闭</button>
        </div>
      </section>
    </div>
  );
}
